import React from "react";
import {
  Typography,
  Box,
  Link,
  Tabs,
  Tab,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
} from "@mui/material";
import { useUser } from "../contexts/useUser";
// Import react-syntax-highlighter components and styles
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
// You can choose the style you prefer for highlighting from the available themes
import { a11yDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { ChatPanel } from "llmasaservice-ui";
import "../../node_modules/llmasaservice-ui/dist/index.css";

const EmbedInstructions: React.FC = () => {
  const { project } = useUser();
  const [tab, setTab] = React.useState(0);
  const [title, setTitle] = React.useState("Your agent");
  const [initialMessage, setInitialMessage] = React.useState(
    "Enter your prompt here"
  );
  const [promptTemplate, setPromptTemplate] = React.useState("{{prompt}}");
  const [promptOrMessage, setPromptOrMessage] = React.useState("message");
  const [actions, setActions] = React.useState("[]");
  const [followonSuggestions, setFollowonSuggestions] = React.useState("");

  const [theme, setTheme] = React.useState<"dark" | "light">("dark");

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(event.target.value);
  };

  const handleInitialMessageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setInitialMessage(event.target.value);
  };

  const handlePromptTemplateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPromptTemplate(event.target.value);
  };

  const handlePromptOrMessageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPromptOrMessage(event.target.value);
  };

  const handleActionsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActions(event.target.value);
  };

  const handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTheme(event.target.value as "dark" | "light");
  };

  const handleFollowonSuggestionsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFollowonSuggestions(event.target.value);
  };

  const escapeUnescapedBackslashes = (str: string): string => {
    return str.replace(/(?<!\\)\\(?!\\)/gim, "\\\\");
  };

  const fixPatternRegexInActions = (actions: string): string => {
    const regex = /"?pattern"?\s*:\s*"(\S*)"/gim;

    // replace all group 1 by adding a second \ for each \ in the group
    const matches = actions.match(regex);
    if (matches) {
      matches.forEach((match) => {
        actions = actions.replace(match, escapeUnescapedBackslashes(match));
      });
    }

    return actions;
  };

  const iframeSrc = `${window.location.origin}/embedchat?id=${
    project?.id
  }&customer_id=&title=${encodeURIComponent(
    title
  )}&template=${encodeURIComponent(
    promptTemplate
  )}&initial=${encodeURIComponent(
    initialMessage
  )}&theme=${theme}&start=${promptOrMessage}&actions=${encodeURIComponent(
    fixPatternRegexInActions(actions)
  )}&followon=${encodeURIComponent(followonSuggestions)}`;

  const iframeCode = `<iframe
  src="${iframeSrc}"
  width="500"
  height="500"
  style="border:none;"
  allow="clipboard-read *; clipboard-write *"
  ></iframe>`;

  const handleCopyToClipboard = () => {
    navigator.clipboard
      .writeText(iframeCode)
      .then(() => {
        alert("Copied to clipboard!");
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  return (
    <Box>
      <Tabs
        value={tab}
        onChange={(event, newValue) => setTab(newValue)}
        aria-label="Embedding Instructions Tabs"
      >
        <Tab label="Streaming Client (NPM)" />
        <Tab label="Direct Fetch (node)" />
        <Tab label="Python Fetch" />
        <Tab label="UI Panel" />
      </Tabs>

      {tab === 0 && (
        <Box m={2}>
          <Typography variant="body2">
            See full instruction on NPM:{" "}
            <Link
              href="https://www.npmjs.com/package/llmasaservice-client"
              target="_blank"
            >
              llmasaservice-client
            </Link>
            <br />
            <br />
          </Typography>

          <Typography variant="body2">
            Step 1: Install the package using npm or yarn.
          </Typography>

          <Typography variant="body2">
            {/* Using SyntaxHighlighter for syntax highlighting */}
            <SyntaxHighlighter language="bash" style={a11yDark}>
              {`# for npm
npm install llmasaservice-client
      
# for yarn
yarn add llmasaservice-client`}
            </SyntaxHighlighter>
          </Typography>

          <Typography variant="body2">
            Step 2: import and use the useLLM hook initialized with your project
            id, and optional customer details.
          </Typography>

          <Typography
            variant="body2"
            sx={{ backgroundColor: "black", fontFamily: "monospace" }}
          >
            <SyntaxHighlighter
              wrapLongLines={true}
              language="javascript"
              style={a11yDark}
            >
              {`"use client"; // only for next.js

// import the hook
import { useLLM } from "llmasaservice-client";

export default function Home() {

  // initialize the hook with your project id
  const { send, response, idle } = useLLM({
    project_id: "${project?.id}", // this is unique to YOUR project!
    
    // optional - identifies your customer in the control panel
    customer: { 
      customer_id: "123", 
      customer_name: "Our Best Customer, Inc.", 
    }
  });

  const sendChat = async () => {
    const cityInput = document.getElementById("city") as HTMLInputElement;
    if (cityInput) {
      // call send to get responses from your LLMs
      send(
        \`Write a short three sentence background on the city called: \${cityInput.value}\`
      );
    }
  };

  return (
    <>
      <input id="city" type="text" placeholder="Enter city name" />
      <button onClick={sendChat} disabled={!idle}>
        Get short background for city
      </button>
      <p>{response}</p>
    </>
  );
}
`}
            </SyntaxHighlighter>
          </Typography>
        </Box>
      )}

      {tab === 1 && (
        <Box m={2}>
          <Typography variant="body2">
            The chat service can be called with no package or other dependencies
            using a simple fetch post call. This is useful for server-side
            rendering or other use cases where you don't want to use the client
            package.
          </Typography>

          <Typography variant="body2">
            {/* Using SyntaxHighlighter for syntax highlighting */}
            <SyntaxHighlighter language="javascript" style={a11yDark}>
              {`const URL = "https://chat.llmasaservice.io/";
const PROJECT_ID = "${project?.id}"; // this is YOUR project id!

async function callLLMAsAService(
  prompt,
  messages = [
    {
      role: "system",
      content: "Answer in plain text formatting, in a comical way",
    },
  ]
) {
  const responseBody = JSON.stringify({
    projectId: PROJECT_ID,
    prompt: prompt,
    messages: messages,
    customer: {}, // Optional { customer_id: "1234", customer_name: "John Doe" }
  });

  const options = {
    method: "POST",
    headers: {
      "Content-Type": "text/plain",
    },
    body: responseBody,
  };

  try {
    const response = await fetch(URL, options);
    if (!response.ok) {
      console.log(
        \`Error: Network error for service. (\${response.status} \${response.statusText})\`
      );
    } else {
      const reader = response?.body?.getReader();
      const decoder = new TextDecoder("utf-8");

      console.log(await readStreamToEnd(reader, decoder));
    }
  } catch (error) {
    console.log(
      \`Error: Having trouble connecting to chat service. (\${error.message})\`
    );
  }
}

async function readStreamToEnd(reader, decoder) {
  let result = "";

  while (true) {
    const { value, done } = await reader.read();

    if (done) {
      break;
    }

    result += decoder.decode(value);
  }

  return result;
}

const prompt = process.argv[2] || "What are 3 modern boy names?";

// Execute the function
callLLMAsAService(prompt);
`}
            </SyntaxHighlighter>
          </Typography>

          <Typography variant="body2">
            The above code can be run in a node environment using the following
            command:
            <SyntaxHighlighter language="command" style={a11yDark}>
              {`>node index.js

Sure thing! How about these zingers?

1. Maverick - because your child is destined to be a rule-breaker or just really into leather jackets and motorcycles.
2. Asher - sounds like a cool kid who probably knows how to skateboard and will definitely teach you what TikTok is.
3. Jaxon - spelled with an "x" because this generation is all about remixing the classics.`}
            </SyntaxHighlighter>
          </Typography>
        </Box>
      )}

      {tab === 2 && (
        <Box m={2}>
          <Typography variant="body2">
            To make a call to the LLMService.io chat service using Python, you
            can use the following code snippet:
          </Typography>

          <SyntaxHighlighter language="python" style={a11yDark}>
            {`pip install requests`}
          </SyntaxHighlighter>
          <SyntaxHighlighter language="python" style={a11yDark}>
            {`import requests
import json
import sys

URL = "https://chat.llmasaservice.io/"
PROJECT_ID = "${project?.id}"  # this is YOUR project id!

def callLLMAsAService(prompt, messages=[{"role": "system", "content": "Answer in plain text formatting, in a comical way"}]):
    payload = {
        "projectId": PROJECT_ID,
        "prompt": prompt,
        "messages": messages,
        "customer": {},  # Optional { "customer_id": "1234", "customer_name": "John Doe" }
    }

    headers = {
        "Content-Type": "text/plain",
    }

    try:
        response = requests.post(URL, data=json.dumps(payload), headers=headers)
        if response.status_code != 200:
            print(f"Error: Network error for service. ({response.status_code} {response.reason})")
        else:
            print(response.text)
    except requests.RequestException as error:
        print(f"Error: Having trouble connecting to chat service. ({error})")

prompt = sys.argv[1] if len(sys.argv) > 1 else "What are 3 modern boy names?"

# Execute the function
callLLMAsAService(prompt)`}
          </SyntaxHighlighter>

          <SyntaxHighlighter language="command" style={a11yDark}>
            {`>python test.py "3 boy names"
Here's a comical take on three boy names:

1. Bartholomew Bumblebee Butterworth III
(Because nothing says "I'm a sophisticated lad" like a triple B and a Roman numeral)

2. Ziggy Stardust Zamboni
(For the parents who want their kid to be a rock star AND drive the ice resurfacer at hockey games)

3. Chewbacca "Chewy" Chowderpants
(Perfect for the Star Wars fan who also really likes New England cuisine and questionable fashion choices)

Remember, folks, these names come with a lifetime supply of eye-rolls and therapy sessions! Choose wisely! ð`}
          </SyntaxHighlighter>
        </Box>
      )}

      {tab === 3 && (
        <Box m={2}>
          <Typography variant="body2">
            To use the prebuilt UI Chat panel see{" "}
            <Link
              href="https://www.npmjs.com/package/llmasaservice-ui"
              target="_blank"
            >
              llmasaservice-ui
            </Link>{" "}
            and to see documentation more examples:{" "}
            <Link
              href="https://predictabilityatscale.github.io/llmasaservice-ui/?path=/docs/chatpanel--docs"
              target="_blank"
            >
              llmasaservice-ui storybook
            </Link>
            .
          </Typography>

          <SyntaxHighlighter language="curl" style={a11yDark}>
            {`npm i llmasaservice-ui`}
          </SyntaxHighlighter>
          <Typography variant="body2">For a React/Next app:</Typography>

          <SyntaxHighlighter language="javascript" style={a11yDark}>
            {`"use client"; // only for next.js
            
import React from 'react';
import { ChatPanel } from 'llmasaservice-ui';
import "../node_modules/llmasaservice-ui/dist/index.css"; // default styles for light and dark, or replace with your own

const App = () => {
  return (
    <div>
      <h1>My Chat Application</h1>
      <ChatPanel 
        project_id="${project?.id}" // this is YOUR unique project id
        initialPrompt="Give a nice welcome message"
      />
    </div>
  );
};

export default App;`}
          </SyntaxHighlighter>
        </Box>
      )}

    </Box>
  );
};

export default EmbedInstructions;
